import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Banner from "../../components/Banner";
import BannerSlider from "../../components/BannerSlider";
import CasinoLeaderboard from "../../components/HelloGamesTable";
import { Link, useParams, useLocation } from "react-router-dom";
import { useActiveGroups } from "hooks/useGameEngine";
import GroupPage from "../../pages/GroupPage";
import DynamicGroupSwiper from "../../components/DynamicGroupSwiper";
import { useGameEngine, getGameImage } from "hooks/useGameEngine";
import GameImage from '../../components/GameImage';
import * as LucideIcons from "lucide-react";
import styled from "styled-components";
import PromoBanner from "../../components/PromoBanner";
import crazyTime from "../../assets/images/crazy-time.jpg";
import lightningR from "../../assets/images/lightning-roulette.jpg";
import wanted from "../../assets/images/wanted.jpg";
import sugar from "../../assets/images/sugar.jpg";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";

const BannersSection = styled.section`
  width: 100%;
  margin-top: 0px;
  margin-bottom: 24px;
  overflow: hidden;

  @media (max-width: 768px) {
    margin-top: 25px;
    margin-bottom: 12px;
  }

  .swiper {
    width: 100%;
    height: 100%;
  }

  .swiper-slide {
    height: 100%;
  }
`;

const LobbyGamesWrapper = styled.div`
  margin-top: 40px;

  @media (max-width: 768px) {
    margin-top: 15px;
  }
`;

const SportsHome = ({ t, groups }) => {
  const { slug } = useParams();
  const location = useLocation();
  const selectedSport = slug || "all";

  const [searchQuery, setSearchQuery] = useState("");
  const [tempSearchQuery, setTempSearchQuery] = useState("");
  const [visibleSwipers, setVisibleSwipers] = useState(3);
  const [phase, setPhase] = useState("idle");
  const searchTimeoutRef = React.useRef(null);

  useEffect(() => {
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;
    setSearchQuery("");
    setTempSearchQuery("");
    setVisibleSwipers(3);
  }, [slug]);

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setTempSearchQuery(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!value.trim()) {
      setSearchQuery('');
      setPhase('idle');
      return;
    }

    setPhase('loading');
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value.trim());
      setPhase('results');
    }, 600);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter" && searchQuery !== tempSearchQuery) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      setSearchQuery(tempSearchQuery);
      setPhase('results');
    }
  };

  const [banners, setBanners] = useState([]);

  useEffect(() => {
    const fetchBanners = async () => {
      try {
        const { API } = require("utils/Helper");
        const res = await fetch(`${API}/website-banners?type=sports&theme=stake`);
        const json = await res.json();
        if (json && json.success) {
          setBanners(json.data || []);
        }
      } catch (err) {
        console.error("Failed to fetch sports banners:", err);
      }
    };
    fetchBanners();
  }, []);

  const dynamicTabs = (groups || [])
    .filter(g => g.show_as_tab_sports)
    .map(g => ({
      key: g.slug || g.id,
      label: g.name,
      icon: g.icon,
      iconClass: "mdi mdi-gamepad-variant",
      path: `/sports/${g.slug || g.id}`
    }));

  const allCategories = [
    { key: "all", label: "Home", icon: "House", path: "/sports" },
    ...dynamicTabs
  ];

  return (
    <div>
      <Helmet>
        <title>Sports Bet</title>
        <link rel="canonical" href="/sports" />
      </Helmet>

      {/* Shared Banner */}
      {banners && banners.length > 0 && (
        <BannersSection>
          <Swiper
            modules={[Autoplay]}
            slidesPerView={2}
            spaceBetween={16}
            autoplay={{ delay: 4000, disableOnInteraction: false }}
            loop={banners.length > 2}
            style={{ width: '100%', height: '100%' }}
            breakpoints={{
              100: { slidesPerView: 1.08, spaceBetween: 12 },
              769: { slidesPerView: 2, spaceBetween: 16 }
            }}
          >
            {banners.map((b) => {
              const { API } = require("utils/Helper");
              return (
                <SwiperSlide key={b.id}>
                  <PromoBanner
                    tag={b.tag || "Promotion"}
                    title={b.heading || ""}
                    sub={b.subheading || ""}
                    cta={b.button_text || "Bet Now"}
                    grad={b.gradient || "linear-gradient(160deg,#1f5fb5,#0a2848)"}
                    image={b.image_url ? (API + b.image_url) : ""}
                    to={b.button_link || b.link_url}
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </BannersSection>
      )}

      <div className="casino-container">
        {/* Search Bar matching Casino style */}
        <div className="search-container">
          <img
            src="/assets/bcimages/Casino/svgexport-1 (1).svg"
            alt="search"
            className="search-icon"
          />
          <input
            type="text"
            value={tempSearchQuery}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyPress}
            placeholder="Search sports"
            className="search-input"
          />
        </div>

        {/* Filter Tabs matching Casino style */}
        <div className="filter-buttons mt-3 md:mt-4">
          {allCategories.map(({ key, label, iconClass, icon, path }) => {
            const isActive = String(selectedSport) === String(key);
            const isLucideIcon = typeof icon === 'string' && LucideIcons[icon];
            return (
              <Link key={key} to={path} style={{ textDecoration: "none" }}>
                <div className={`filter-button ${isActive ? "active" : ""}`}>
                  {isLucideIcon ? (
                    React.createElement(LucideIcons[icon], {
                      className: "filter-btn-img",
                      style: { 
                        color: isActive ? '#ffffff' : 'inherit',
                        filter: 'none'
                      }
                    })
                  ) : icon ? (
                    <img src={`/assets/bcimages/Casino/${icon}`} className="filter-btn-img" />
                  ) : (
                    <i className={iconClass}></i>
                  )}
                  {label}
                </div>
              </Link>
            );
          })}
        </div>

        {/* Sports / Group Content */}
        <div className="mt-4">
          {selectedSport !== "all" ? (
            <GroupPage groupSlug={selectedSport} hideBanner={true} isLobbyTab={true} lobbySearchQuery={searchQuery} />
          ) : (
            <LobbyGamesWrapper>
              {phase === 'loading' && (
                <div style={{ display: 'grid', placeItems: 'center', padding: '80px 0' }}>
                  <div style={{ width: '48px', height: '6px', backgroundColor: '#0f212e', borderRadius: '99px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: '33%', backgroundColor: 'rgba(255,255,255,0.7)', animation: 'pulse 1.5s ease-in-out infinite' }} />
                  </div>
                </div>
              )}
              {phase !== 'loading' && (
                <>
                  {searchQuery ? (
                    <div>
                      <div style={{ color: '#fff', fontSize: '20px', fontWeight: 'bold', margin: '20px 0' }}>
                        Search Results for "{searchQuery}"
                      </div>
                      <GlobalSearchResults searchQuery={searchQuery} />
                    </div>
                  ) : (
                    <>
                      {(groups || [])
                        .filter((group) => group.show_on_home_sports)
                        .slice(0, visibleSwipers)
                        .map((group) => (
                          <DynamicGroupSwiper
                            key={group.id}
                            group={group}
                            prefix="/sports/group/"
                          />
                        ))}
                    </>
                  )}

                  {(groups || []).filter((group) => group.show_on_home_sports).length > visibleSwipers && !searchQuery && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                      marginTop: '32px',
                      marginBottom: '32px',
                      width: '100%',
                      padding: '0 20px'
                    }}>
                      <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.05)' }} />
                      <button
                        onClick={() => setVisibleSwipers(prev => prev + 3)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#9ca3af',
                          fontSize: '13px',
                          fontWeight: '900',
                          letterSpacing: '0.15em',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                          transition: 'color 0.2s'
                        }}
                        onMouseOver={(e) => e.target.style.color = '#ffffff'}
                        onMouseOut={(e) => e.target.style.color = '#9ca3af'}
                      >
                        LOAD MORE
                      </button>
                      <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255, 255, 255, 0.05)' }} />
                    </div>
                  )}
                </>
              )}
            </LobbyGamesWrapper>
          )}
        </div>

        <CasinoLeaderboard />
      </div>
    </div>
  );
};

function GlobalSearchResults({ searchQuery }) {
  const { data: games, isLoading } = useGameEngine({ search: searchQuery, limit: 100 });

  if (isLoading) return <div style={{ color: '#94a6b5' }}>Searching...</div>;
  if (!games || games.length === 0) return <div style={{ color: '#ff5a7a' }}>No games found.</div>;

  return (
    <div style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
      gap: '12px'
    }}>
      {games.map((g, i) => (
        <div key={i} style={{ backgroundColor: '#0f212e', borderRadius: '8px', overflow: 'hidden', position: 'relative', aspectRatio: '3/4', border: '1px solid rgba(255,255,255,0.05)' }}>
          <a href={`/play/${g.id}`}>
            <GameImage src={getGameImage(g)} alt={g.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </a>
        </div>
      ))}
    </div>
  );
}

export function SportsHomeWrapper(props) {
  const { data: groups } = useActiveGroups();
  return <SportsHome {...props} groups={groups || []} />;
}

export default SportsHomeWrapper;
